import { Linking, Platform } from 'react-native';

import type { RecordingDownloadResponse } from '../../types/auth';

/**
 * CORE-2046 — «Скачать запись» для владельца записи.
 *
 * Запись в хранилище — HLS по фрагментам (CORE-1909), и без нашего плеера её
 * не открыть. Поэтому сервер по первому запросу собирает один обычный файл
 * (MP4, для «Только звук» — M4A), а пока собирает, отвечает `preparing`: тогда
 * пункт говорит «Готовим файл…», и клиент спрашивает снова. Готовый файл
 * приходит короткой подписанной ссылкой, которую браузер сохраняет, а не
 * открывает. Скачивание ничего не меняет — ни публикацию, ни просмотры.
 *
 * Перед скачиванием — короткое напоминание: в записи голоса (и видео) других
 * участников.
 */

export const RECORDING_DOWNLOAD_ACTION = 'Скачать запись';
export const RECORDING_DOWNLOAD_PREPARING = 'Готовим файл…';
export const RECORDING_DOWNLOAD_CONFIRM = 'Скачать';
export const RECORDING_DOWNLOAD_TITLE = 'Скачать запись?';

/** Текст согласия — до скачивания, всегда. */
export function downloadConsentNotice(audioOnly: boolean): string {
  return audioOnly
    ? 'Запись содержит голоса участников — используйте её только с их согласия.'
    : 'Запись содержит голоса и видео участников — используйте её только с их согласия.';
}

const NOT_READY_HINT = 'Скачать можно, когда запись обработается.';
const FAILED_HINT = 'Запись не сохранилась — скачивать нечего.';

/**
 * Почему пункт недоступен; `null` — доступен. Принимает и состояние архива
 * (`ready`), и состояние комнаты (`Ready`).
 */
export function downloadDisabledHint(state: string | null | undefined): string | null {
  switch ((state ?? '').toLowerCase()) {
    case 'ready': return null;
    case 'failed': return FAILED_HINT;
    case 'none':
    case '': return 'Записи нет — скачивать нечего.';
    default: return NOT_READY_HINT;
  }
}

/** Ответ `unavailable` — человеческими словами. */
export function unavailableMessage(reason: RecordingDownloadResponse['reason']): string {
  switch (reason) {
    case 'not_ready': return NOT_READY_HINT;
    case 'failed': return FAILED_HINT;
    case 'no_file': return 'Для этой записи нет файла, который можно скачать.';
    case 'prepare_failed': return 'Не получилось подготовить файл. Напишите в поддержку — разберёмся.';
    default: return 'Скачать запись сейчас не получится. Попробуйте позже.';
  }
}

export const DOWNLOAD_POLL_MS = 5_000;
/** Сколько ждать сборки файла, прежде чем предложить вернуться позже. */
export const DOWNLOAD_WAIT_MS = 20 * 60_000;
export const STILL_PREPARING_MESSAGE = 'Файл ещё готовится. Попробуйте скачать чуть позже — он будет ждать.';

/**
 * Ручки на бэкенде ещё нет (фронт вышел раньше): 404 — и пункт прячется до
 * перезагрузки. Модуль, а не состояние: меню у каждой строки своё.
 */
let endpointMissing = false;
export const isDownloadEndpointMissing = () => endpointMissing;
export const markDownloadEndpointMissing = () => { endpointMissing = true; };
/** Только для тестов. */
export const resetDownloadEndpointMissing = () => { endpointMissing = false; };

export type DownloadOutcome =
  | { kind: 'ready'; url: string }
  | { kind: 'preparing' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'missing' }
  | { kind: 'error'; error: unknown };

/** Один ответ сервера (или его ошибка) — в следующий шаг клиента. */
export function classifyDownload(result: RecordingDownloadResponse | null, error?: unknown): DownloadOutcome {
  if (error) {
    return (error as { status?: number } | null)?.status === 404 ? { kind: 'missing' } : { kind: 'error', error };
  }
  if (result?.status === 'ready' && result.url) return { kind: 'ready', url: result.url };
  if (result?.status === 'preparing') return { kind: 'preparing' };
  return { kind: 'unavailable', message: unavailableMessage(result?.reason) };
}

/**
 * Отдать файл. В вебе — переход по ссылке: сервер отвечает `attachment`, и
 * браузер сохраняет файл, оставаясь на странице; новое окно после ожидания
 * сборки заблокировал бы блокировщик всплывающих окон. На телефоне ссылку
 * открывает системный браузер.
 */
export function openRecordingDownload(url: string): void {
  if (Platform.OS === 'web') {
    window.location.assign(url);
    return;
  }
  void Linking.openURL(url);
}
